import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/contexts/TenantContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { getChatwootSDK } from "@/integrations/chatwoot/sdk";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const schema = z.object({
  companyName: z.string().min(2, "Informe o nome da empresa"),
  supportEmail: z.string().email("Email inválido"),
  adminName: z.string().min(2, "Informe o nome do admin"),
  adminPassword: z.string().min(6, "Crie uma senha com pelo menos 6 caracteres"),
});

type FormData = z.infer<typeof schema>;

export default function Chatwoot() {
  const { tenantName, refreshTenant, tenantId } = useTenant();
  const queryClient = useQueryClient();
  const [existingToken, setExistingToken] = useState("");
  const [hasStoredToken, setHasStoredToken] = useState(false);
  const [savingToken, setSavingToken] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [chatwootAccountId, setChatwootAccountId] = useState<number | null>(null);
  const [chatwootUserId, setChatwootUserId] = useState<number | null>(null);
  const [ssoLink, setSsoLink] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [availableAccounts, setAvailableAccounts] = useState<Array<{ account_id: number; role: string; name?: string }>>([]);

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      companyName: tenantName || "",
      supportEmail: "",
      adminName: "",
      adminPassword: "",
    },
  });

  // Atualiza nome da empresa quando o contexto carregar
  useEffect(() => {
    if (tenantName) {
      form.setValue("companyName", tenantName);
    }
  }, [tenantName, form]);

  // Carrega email e nome do usuário logado + dados do Chatwoot
  useEffect(() => {
    const loadUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) {
        form.setValue("supportEmail", user.email);
      }

      // tentar obter nome do profile
      if (user?.id) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("nome_completo, chatwoot_user_id, chatwoot_user_token, tenant_id")
          .eq("id", user.id)
          .maybeSingle();
        if (profile?.nome_completo) {
          form.setValue("adminName", profile.nome_completo);
        } else {
          // fallback: nome do metadata, se existir
          const fullName = (user.user_metadata as any)?.full_name || (user.user_metadata as any)?.name;
          if (fullName) form.setValue("adminName", fullName);
        }

        if (profile?.chatwoot_user_id) {
          setChatwootUserId(profile.chatwoot_user_id);
        }

        setHasStoredToken(Boolean(profile?.chatwoot_user_token));

        if (profile?.tenant_id) {
          const { data: tenant } = await supabase
            .from("tenants" as any)
            .select("chatwoot_account_id")
            .eq("id", profile.tenant_id)
            .maybeSingle();

          if (tenant && (tenant as any).chatwoot_account_id) {
            setChatwootAccountId((tenant as any).chatwoot_account_id);
          }
        }

        if (profile?.chatwoot_user_id) {
          try {
            const sdk = getChatwootSDK();
            const res = await sdk.listUserAccounts(profile.chatwoot_user_id);
            setAvailableAccounts(res.accounts || []);
          } catch (e) {
            console.warn("Falha ao listar accounts do usuário", e);
          }
        }

        // Fallback: se já há token salvo, tentar obter o account via Application API profile
        if (profile?.chatwoot_user_token) {
          try {
            const sdk = getChatwootSDK();
            const prof: any = await sdk.appProxy({
              path: `/api/v1/profile`,
              method: "GET",
              token: profile.chatwoot_user_token,
            });
            const accountsArr = Array.isArray(prof?.accounts) ? prof.accounts : [];
            const mapped = accountsArr
              .map((a: any) => ({ account_id: Number(a?.id ?? a?.account_id), role: String(a?.role ?? "administrator"), name: a?.name }))
              .filter((a: any) => a.account_id > 0);
            if (mapped.length > 0) setAvailableAccounts(mapped);
            else {
              const accId = Number(prof?.account_id || prof?.account?.id);
              const accName = prof?.account?.name || undefined;
              if (accId) setAvailableAccounts([{ account_id: accId, role: "administrator", name: accName }]);
            }
          } catch (e) {
            console.warn("Falha no fallback de profile para accounts", e);
          }
        }
      }
    };
    loadUser();
  }, [form]);

  const parseChatwootError = (rawError: any) => {
    const fallback = "Não foi possível concluir a operação no Chatwoot. Verifique os dados e tente novamente.";
    const baseMessage = typeof rawError === "string" ? rawError : rawError?.message || rawError?.error || fallback;

    let message = baseMessage;
    let field: keyof FormData | undefined;

    if (typeof message === "string") {
      const braceStart = message.indexOf("{");
      const braceEnd = message.lastIndexOf("}");
      if (braceStart !== -1 && braceEnd !== -1 && braceEnd > braceStart) {
        const jsonSlice = message.slice(braceStart, braceEnd + 1);
        try {
          const parsed = JSON.parse(jsonSlice);
          if (parsed?.message) {
            message = parsed.message;
          }
          if (Array.isArray(parsed?.attributes)) {
            if (parsed.attributes.includes("password")) {
              field = "adminPassword";
            } else if (parsed.attributes.includes("support_email") || parsed.attributes.includes("email")) {
              field = "supportEmail";
            }
          }
        } catch (err) {
          console.warn("Falha ao interpretar erro do Chatwoot", err);
        }
      }

      if (message.includes("Password must")) {
        message = "A senha precisa ter pelo menos 1 letra maiúscula, 1 número e 1 caractere especial.";
        field = "adminPassword";
      }
      if (message.toLowerCase().includes("support email")) {
        message = "Este e-mail já está vinculado como suporte no Chatwoot. Utilize o token existente para conectar.";
        field = "supportEmail";
      }
      if (message.toLowerCase().includes("email has already been taken")) {
        message = "Este e-mail já possui usuário no Chatwoot. Utilize o token existente para conectar.";
        field = "supportEmail";
      }
    }

    return { message, field };
  };

  const onSubmit = async (data: FormData) => {
    setFormError(null);
    form.clearErrors();
    setSsoLink(null);

    if (chatwootAccountId && chatwootUserId) {
      const message = "Este e-mail já está cadastrado no Chatwoot. Cole o token no campo abaixo e conecte sua conta.";
      setFormError(message);
      toast.error(message);
      return;
    }

    try {
      setSubmitting(true);
      const sdk = getChatwootSDK();
      let currentAccountId = chatwootAccountId;

      if (!currentAccountId) {
        const account = await sdk.createAccount({
          name: data.companyName,
          support_email: data.supportEmail,
          locale: "pt_BR",
        });

        currentAccountId = account.account_id;
        setChatwootAccountId(account.account_id);
        await refreshTenant();
      }

      if (chatwootUserId) {
        const message = "Este usuário já possui acesso ao Chatwoot. Utilize o token da conta para conectar.";
        setFormError(message);
        toast.error(message);
        return;
      }

      const user = await sdk.createUser({
        name: data.adminName,
        display_name: data.adminName,
        email: data.supportEmail,
        password: data.adminPassword,
      });

      setChatwootUserId(user.chatwoot_user_id);

      await sdk.linkAccountUser({ user_id: user.chatwoot_user_id, role: "administrator" });

      const { url } = await sdk.getUserSsoLink(user.chatwoot_user_id);
      if (url) {
        setSsoLink(url);
      }

      toast.success("Conta Chatwoot criada com sucesso! Use o botão para acessar sua conta.");
    } catch (error: any) {
      console.error("Erro ao provisionar Chatwoot", error);
      const parsed = parseChatwootError(error);
      setFormError(parsed.message);
      toast.error(parsed.message);
      if (parsed.field) {
        form.setError(parsed.field, { type: "manual", message: parsed.message });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const leftContent = useMemo(() => (
    <Card>
      <CardHeader>
        <CardTitle>O que é o Chatwoot?</CardTitle>
        <CardDescription>
          Plataforma para atendimento multi atendente com foco em WhatsApp.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm text-muted-foreground">
        <p>
          Centralize seu atendimento do WhatsApp em uma única caixa de entrada com múltiplos atendentes,
          histórico de conversas, tags, notas internas e muito mais.
        </p>
        <div>
          <p className="font-medium text-foreground mb-2">Vantagens:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Multi atendente em um único WhatsApp</li>
            <li>Transferência e acompanhamento de conversas</li>
            <li>Fila, tags, notas internas e menções</li>
            <li>Automação e respostas rápidas</li>
            <li>Relatórios e indicadores de atendimento</li>
          </ul>
        </div>
        <div>
          <p className="font-medium text-foreground mb-2">Para quem serve:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Comércios e serviços com alto volume de mensagens</li>
            <li>Times de suporte, vendas e pós-venda</li>
            <li>Empresas que precisam organizar WhatsApp</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  ), []);

  return (
    <div className="space-y-6" data-cy="chatwoot-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" data-cy="chatwoot-title">
          Chatwoot - Plataforma de atendimento para WhatsApp
        </h1>
        <p className="text-muted-foreground" data-cy="chatwoot-subtitle">
          Gestione de forma inteligênte o atendimento WhatsApp com multi atendentes e muito mais
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1">{leftContent}</div>
        <div className="md:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Crie sua conta no Chatwoot</CardTitle>
              <CardDescription>
                É simples: confira seus dados abaixo e toque em "Criar Conta". Nada técnico, bem fácil.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!hasStoredToken && !chatwootUserId ? (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" data-cy="chatwoot-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="companyName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Nome da sua empresa</FormLabel>
                            <FormControl>
                              <Input placeholder="Ex.: Padaria Central" {...field} data-cy="chatwoot-company" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="supportEmail"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Seu e-mail de contato</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="Ex.: contato@empresa.com" {...field} data-cy="chatwoot-email" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="adminName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Seu nome</FormLabel>
                            <FormControl>
                              <Input placeholder="Ex.: Maria Silva" {...field} data-cy="chatwoot-admin" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="adminPassword"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Senha de acesso</FormLabel>
                            <FormControl>
                              <Input
                                type="password"
                                placeholder="Escolha uma senha fácil de lembrar"
                                {...field}
                                data-cy="chatwoot-password"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {formError && (
                      <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                        {formError}
                      </div>
                    )}

                    <Button type="submit" className="w-full" data-cy="chatwoot-submit" disabled={submitting}>
                      {submitting ? "Criando conta..." : "Criar Conta Chatwoot"}
                    </Button>
                  </form>
                </Form>
              ) : (
                <div className="rounded-md border border-muted-foreground/20 bg-muted p-4 text-sm text-muted-foreground space-y-3">
                  <div>
                    <p className="font-medium text-foreground">Conta Chatwoot detectada</p>
                    <p>
                      Já identificamos um token salvo. Se preferir usar outro e-mail ou criar uma conta nova,
                      remova o token abaixo primeiro.
                    </p>
                  </div>
                  <div className="flex flex-col gap-3 md:flex-row md:items-center">
                    <Button
                      type="button"
                      variant="outline"
                      className="md:w-auto"
                      disabled={savingToken}
                      onClick={async () => {
                        try {
                          setSavingToken(true);
                          const sdk = getChatwootSDK();
                          await sdk.saveUserToken(null);
                          setHasStoredToken(false);
                          setExistingToken("");
                          setChatwootUserId(null);
                          setChatwootAccountId(null);
                          setSsoLink(null);
                          setAvailableAccounts([]);
                          toast.success("Conta desconectada. Você pode adicionar um novo token abaixo.");
                        } catch (error: any) {
                          toast.error(error?.message || "Não foi possível desconectar o Chatwoot.");
                        } finally {
                          setSavingToken(false);
                        }
                      }}
                      data-cy="chatwoot-disconnect"
                    >
                      Desconectar conta
                    </Button>

                    {availableAccounts.length > 1 ? (
                      <select
                        className="border rounded-md p-2 text-sm md:w-auto flex-1"
                        value={String(chatwootAccountId || "")}
                        onChange={async (e) => {
                          const newId = Number(e.target.value);
                          setChatwootAccountId(newId);
                          if (!newId) {
                            toast.error("Selecione um Account");
                            return;
                          }
                          try {
                            const { data: { user } } = await supabase.auth.getUser();
                            const { data: profile } = await supabase
                              .from("profiles")
                              .select("tenant_id")
                              .eq("id", user?.id)
                              .maybeSingle();
                            if (!profile?.tenant_id) {
                              toast.error("Tenant não encontrado");
                              return;
                            }
                            const { error } = await supabase
                              .from("tenants" as any)
                              .update({ chatwoot_account_id: newId })
                              .eq("id", profile.tenant_id);
                            if (error) throw error;
                            toast.success("Conta chatwoot selecionada para o tenant");
                            await refreshTenant();
                            queryClient.invalidateQueries({ queryKey: ["chatwoot-config", tenantId ?? profile.tenant_id] });
                          } catch (e: any) {
                            toast.error(e.message || "Erro ao salvar account");
                          }
                        }}
                      >
                        <option value="">Selecione o Account…</option>
                        {availableAccounts.map((a) => (
                          <option key={a.account_id} value={String(a.account_id)}>{`${a.account_id} · ${a.name || "Account"} (${a.role})`}</option>
                        ))}
                      </select>
                    ) : availableAccounts.length === 1 ? (
                      <span className="text-sm text-foreground flex-1">{`${availableAccounts[0].account_id} · ${availableAccounts[0].name || "Account"} (${availableAccounts[0].role})`}</span>
                    ) : null}
                  </div>
                  
                </div>
              )}

              {ssoLink && (
                <div className="pt-4">
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full"
                    onClick={() => window.open(ssoLink, "_blank")}
                    data-cy="chatwoot-open-sso"
                  >
                    Acessar Chatwoot
                  </Button>
                </div>
              )}

              <div className="space-y-3 pt-6 border-t mt-6">
                <p className="text-sm text-muted-foreground">
                  {hasStoredToken
                    ? "Quer conectar a outra conta Chatwoot? Adicione o novo token para alterar:"
                    : "Já possui conta Chatwoot? Adicione seu token:"}
                </p>
                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                  <Input
                    placeholder="Cole aqui o token da sua conta"
                    value={existingToken}
                    onChange={(event) => setExistingToken(event.target.value)}
                    data-cy="chatwoot-token"
                  />
                  
                  <Button
                    type="button"
                    variant="outline"
                    className="md:w-auto"
                    disabled={savingToken || existingToken.trim().length === 0}
                    onClick={async () => {
                      const token = existingToken.trim();
                      if (!token) {
                        toast.warning("Informe o token para conectar sua conta.");
                        return;
                      }
                      try {
                        setSavingToken(true);
                        const sdk = getChatwootSDK();
                        const result = await sdk.saveUserToken(token);
                        let selectedId: number | null = chatwootAccountId || null;
                        if (result.chatwoot_user_id) {
                          setChatwootUserId(result.chatwoot_user_id);
                          try {
                            const res = await sdk.listUserAccounts(result.chatwoot_user_id);
                            const accs = res.accounts || [];
                            setAvailableAccounts(accs);
                            if (accs.length === 1) selectedId = Number(accs[0]?.account_id);
                          } catch {}
                        }
                        if (result.chatwoot_account_id) {
                          selectedId = Number(result.chatwoot_account_id);
                        }
                        if ((availableAccounts || []).length === 0) {
                          try {
                            const prof: any = await sdk.appProxy({
                              path: `/api/v1/profile`,
                              method: "GET",
                              token,
                            });
                            const accountsArr = Array.isArray(prof?.accounts) ? prof.accounts : [];
                            const mapped = accountsArr
                              .map((a: any) => ({ account_id: Number(a?.id ?? a?.account_id), role: String(a?.role ?? "administrator"), name: a?.name }))
                              .filter((a: any) => a.account_id > 0);
                            if (mapped.length > 0) {
                              setAvailableAccounts(mapped);
                              if (mapped.length === 1) selectedId = Number(mapped[0].account_id);
                            } else {
                              const accId = Number(prof?.account_id || prof?.account?.id);
                              const accName = prof?.account?.name || undefined;
                              if (accId) {
                                setAvailableAccounts([{ account_id: accId, role: "administrator", name: accName }]);
                                selectedId = accId;
                              }
                            }
                          } catch {}
                        }
                        if (selectedId) {
                          const { data: { user } } = await supabase.auth.getUser();
                          const { data: profile } = await supabase
                            .from("profiles")
                            .select("tenant_id")
                            .eq("id", user?.id)
                            .maybeSingle();
                          if (profile?.tenant_id) {
                            const { error } = await supabase
                              .from("tenants" as any)
                              .update({ chatwoot_account_id: selectedId })
                              .eq("id", profile.tenant_id);
                            if (!error) {
                              setChatwootAccountId(selectedId);
                              await refreshTenant();
                              queryClient.invalidateQueries({ queryKey: ["chatwoot-config", tenantId ?? profile.tenant_id] });
                            }
                          }
                        }
                        setHasStoredToken(true);
                        setExistingToken("");
                        toast.success("Token salvo e sincronizado com sucesso!");
                      } catch (e: any) {
                        toast.error(`Erro ao salvar token: ${e?.message || "tente novamente"}`);
                      } finally {
                        setSavingToken(false);
                      }
                    }}
                    data-cy="chatwoot-token-submit"
                  >
                    {savingToken ? "Conectando..." : "Conectar minha conta"}
                  </Button>
                </div>
                

                <div>
                  <a href="https://chatwoot.servinder.com.br/app/accounts/13/profile/settings">
                    Obtenha o token de usuário neste link
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
